import { FC, useEffect } from 'react';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { ICareMapNoteTarget } from '../model/ICareMapNoteTarget';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';
import { createUseStyles } from 'react-jss';
import { ViewNoteMenu } from './ViewNoteMenu';
import { NoteDateTarget } from '../note-list/CareMapNotesListTitle';
import { UserNotes } from './UserNotes';
import { Overview } from './Overview';
import { SmartTasks } from './SmartTasks';
import { Attachments } from './Attachments';
import { DetailedSummary } from './DetailedSummary';
import { Transcription } from './Transcription';
import { Participants } from './Participants';
import { AppointmentLink } from './AppointmentLink';
import { AppointmentsState } from '../../appointments/AppointmentsState';
import { TagsSection } from './TagsSection';

const useStyles = createUseStyles({
    stretchHeight: {
        display: 'flex',
        flexDirection: 'column'
    }
});

interface IViewNoteUsingCareMapDrawerProps {
    noteId: string;
    defaultNoteTarget?: ICareMapNoteTarget;
}

export const ViewNoteUsingCareMapDrawer: FC<IViewNoteUsingCareMapDrawerProps> = ({
    noteId
}) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const tracker = useInstance(CareMapNoteTracker);
    const appointmentsState = useInstance(AppointmentsState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    // Get appointment if linked through recording
    const appointmentId = note?.otherAttributes?.recording?.appointmentId;
    const appointment = appointmentId ? appointmentsState.getAppointment(appointmentId) : undefined;

    useEffect(() => {
        if (note) {
            tracker.onNoteViewed(note, 'caremap-drawer');
        }
    }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        return;
    }

    const canEdit = model.canEdit(note);

    // Create a custom edit handler that navigates to the edit route within caremap drawer
    const editHandler = () => {
        const currentPath = history.getCurrentLocation().pathname;
        // Navigate to the edit route by appending /edit to current path
        history.push(`${currentPath}/edit`);
    };

    const handleAppointmentClick = () => {
        if (appointment) {
            // Navigate to appointment within caremap drawer context
            const basePath = history.getCurrentLocation().pathname.split('/').slice(0, -2).join('/');
            history.push(`${basePath}/appointments/${appointment.appointmentId}`);
        }
    };

    return (
        <CareMapDrawerPage
            title="View Note"
            hasBack={true}
            data-type="view-note-caremap-drawer"
        >
            <div data-type="view-note" data-note-id={note.noteId} className={classes.stretchHeight}>

                <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                    <div style={{fontSize: 20, fontWeight: 'bold'}}>{note.title}</div>
                    <ViewNoteMenu model={model} noteId={noteId} trackingFrom="caremap-drawer" customEditHandler={editHandler} />
                </div>

                <NoteDateTarget note={note} showNoteTargetHeader showBothDates />

                <UserNotes
                    content={note.html}
                    editHandler={canEdit ? editHandler : undefined}
                    hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                />

                {appointment && (
                    <AppointmentLink
                        appointment={appointment}
                        onClick={handleAppointmentClick}
                    />
                )}

                <Overview note={note} />

                <Participants note={note} />

                <DetailedSummary note={note} />

                <SmartTasks note={note} />

                <Attachments
                    note={note}
                    editHandler={canEdit ? editHandler : undefined}
                />

                <TagsSection note={note} />

                <Transcription note={note} />
            </div>
        </CareMapDrawerPage>
    );
};
